// ─── ExternalResearch.jsx ────────────────────────────────────────────────────
// Shared OSINT / external research panel used in CaseDetail, EDDPage,
// and KYCOnboardingPortal.
//
// Features:
//  • Web / business search (SerpAPI via server proxy)
//  • Adverse news (negative news, regulatory actions, fraud)
//  • Court records (site-targeted Google search)
//  • Social media profiles
//  • Email breach check (HaveIBeenPwned)
//  • Phone validation (Abstract API)
//  • AI synthesis of all findings → risk-rated assessment
//
// API keys (all server-side Railway env vars):
//  • SERPAPI_KEY, HIBP_API_KEY, ABSTRACT_PHONE_KEY

import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";

const PROXY = import.meta.env.VITE_PROXY_URL || "http://127.0.0.1:3001";

// ─── HELPERS ──────────────────────────────────────────────────────────────────

async function webSearch(query, type) {
  const r = await fetch(`${PROXY}/api/websearch`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body:    JSON.stringify({ query, type, num: 8 }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Search error ${r.status}`);
  }
  return r.json(); // { results: [...], query, type }
}

async function hibpLookup(email) {
  const r = await fetch(`${PROXY}/api/hibp`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body:    JSON.stringify({ email }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || `HIBP error ${r.status}`);
  return data; // { breaches: [...], clean: bool }
}

async function phoneLookup(phone) {
  const r = await fetch(`${PROXY}/api/phonelookup`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body:    JSON.stringify({ phone }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || `Phone lookup error ${r.status}`);
  return data;
}

async function aiSynthesize(subjectName, subjectType, subjectCountry, allResults) {
  const format = (label, items) => {
    if (!items?.length) return `${label}: No results found.`;
    return `${label}:\n${items.slice(0, 5).map(i => `  • [${i.source || ""}] ${i.title}: ${i.snippet?.slice(0, 180) || ""}`).join("\n")}`;
  };

  const prompt = `You are a senior AML/KYC compliance analyst. You have run external OSINT research on the following subject and received the results below. Provide a structured risk assessment.

SUBJECT: ${subjectName}
TYPE: ${subjectType || "Individual"}
COUNTRY: ${subjectCountry || "Unknown"}

RESEARCH FINDINGS:
${format("Web / Business Search", allResults.web)}
${format("Adverse News & Negative Media", allResults.news)}
${format("Court Records & Legal Proceedings", allResults.court)}
${format("Social Media Profiles", allResults.social)}
${allResults.hibp ? (allResults.hibp.clean ? "Email Breach Check (HIBP): No breaches found." : `Email Breach Check (HIBP): ${allResults.hibp.breaches.length} breach(es) found — ${allResults.hibp.breaches.slice(0, 5).map(b => b.Name).join(", ")}`) : "Email Breach Check: Not run."}
${allResults.phone ? `Phone Intelligence: ${allResults.phone.phone_validation?.is_valid ? `Valid — ${allResults.phone.phone_carrier?.line_type || ""} line, carrier: ${allResults.phone.phone_carrier?.name || ""}, country: ${allResults.phone.phone_location?.country_name || ""}, risk: ${allResults.phone.phone_risk?.risk_level || "unknown"}${allResults.phone.phone_validation?.is_voip ? ", VoIP" : ""}` : "Invalid/unallocated number"}` : "Phone Intelligence: Not run."}

Provide your assessment in this exact structure:
1. OVERALL RISK RATING: [Low / Medium / High / Critical] — one sentence justification
2. KEY FINDINGS: bullet list of the most significant findings (3–6 bullets)
3. ADVERSE MEDIA SUMMARY: summarise any negative news, regulatory actions, or criminal allegations found
4. COURT & LEGAL HISTORY: summarise any litigation, judgments, or enforcement actions found
5. SOCIAL MEDIA & ONLINE PRESENCE: summarise the subject's digital footprint and any red flags
6. RECOMMENDED ACTIONS: 2–4 specific compliance actions (e.g. EDD required, SAR consideration, MLRO escalation)

Be concise and precise. Flag clearly if no adverse findings were found in a category.`;

  const r = await fetch(`${PROXY}/api/ai`, {
    method:  "POST",
    signal:  AbortSignal.timeout(90000),
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body:    JSON.stringify({
      system:     "You are a senior AML/KYC compliance analyst. Be direct, specific, and actionable. Plain text only — no markdown symbols or asterisks.",
      messages:   [{ role: "user", content: prompt }],
      max_tokens: 1000,
    }),
  });
  if (!r.ok) throw new Error("AI synthesis failed");
  const data = await r.json();
  return data.content?.[0]?.text || data.choices?.[0]?.message?.content || "";
}

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

function ResultCard({ title, icon, status, results, error, C }) {
  const [open, setOpen] = useState(true);

  const statusEl = status === "loading" ? (
    <span style={{ color: C.textDim, fontSize: 11 }}>⏳ Searching…</span>
  ) : status === "error" ? (
    <span style={{ color: C.danger, fontSize: 11 }}>✗ {error}</span>
  ) : status === "done" ? (
    <span style={{ color: results?.length ? C.warn : C.success, fontSize: 11 }}>
      {results?.length ? `${results.length} result${results.length !== 1 ? "s" : ""} found` : "✓ Nothing found"}
    </span>
  ) : (
    <span style={{ color: C.textDim, fontSize: 11 }}>Not yet run</span>
  );

  return (
    <div style={{ background: C.surface, border: `1px solid ${status === "error" ? C.danger + "44" : results?.length ? C.warn + "33" : C.border}`, borderRadius: 8, overflow: "hidden" }}>
      <div onClick={() => status === "done" && results?.length && setOpen(o => !o)}
        style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 8,
          cursor: status === "done" && results?.length ? "pointer" : "default" }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ color: C.text, fontSize: 12, fontWeight: 600, flex: 1 }}>{title}</span>
        {statusEl}
        {status === "done" && results?.length > 0 && (
          <span style={{ color: C.textDim, fontSize: 11 }}>{open ? "▲" : "▼"}</span>
        )}
      </div>
      {open && status === "done" && results?.length > 0 && (
        <div style={{ borderTop: `1px solid ${C.border}`, maxHeight: 320, overflowY: "auto" }}>
          {results.map((r, i) => {
            // Use r.link if available, otherwise construct a search link
            const href = r.link || `https://www.google.com/search?q=${encodeURIComponent(r.title || r.snippet || "")}`;
            return (
              <div key={i} style={{ padding: "10px 14px", borderBottom: i < results.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                  <a href={href} target="_blank" rel="noopener noreferrer"
                    style={{ color: C.accent, fontSize: 12, fontWeight: 600, textDecoration: "none", cursor: "pointer",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "80%" }}
                    onClick={e => e.stopPropagation()}
                    title={r.title || href}>
                    {r.title || href}
                  </a>
                  {r.date && <span style={{ color: C.textDim, fontSize: 10, flexShrink: 0 }}>{r.date}</span>}
                </div>
                {r.source && <div style={{ color: C.textDim, fontSize: 10, marginBottom: 3 }}>{r.source}</div>}
                {r.snippet && <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6 }}>{r.snippet}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HIBPCard({ status, data, error, C }) {
  const [open, setOpen] = useState(true);
  const breaches = data?.breaches || [];
  const clean    = data?.clean;

  const statusEl = status === "loading" ? (
    <span style={{ color: C.textDim, fontSize: 11 }}>⏳ Checking…</span>
  ) : status === "error" ? (
    <span style={{ color: C.danger, fontSize: 11 }}>✗ {error}</span>
  ) : status === "done" ? (
    <span style={{ color: clean ? C.success : C.danger, fontSize: 11 }}>
      {clean ? "✓ No breaches found" : `⚠ ${breaches.length} breach${breaches.length !== 1 ? "es" : ""} found`}
    </span>
  ) : (
    <span style={{ color: C.textDim, fontSize: 11 }}>Not yet run</span>
  );

  return (
    <div style={{ background: C.surface, border: `1px solid ${status === "done" && !clean ? C.danger + "44" : C.border}`, borderRadius: 8, overflow: "hidden" }}>
      <div onClick={() => status === "done" && breaches.length && setOpen(o => !o)}
        style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 8,
          cursor: status === "done" && breaches.length ? "pointer" : "default" }}>
        <span style={{ fontSize: 15 }}>✉</span>
        <span style={{ color: C.text, fontSize: 12, fontWeight: 600, flex: 1 }}>Email Breach Check (HaveIBeenPwned)</span>
        {statusEl}
        {status === "done" && breaches.length > 0 && (
          <span style={{ color: C.textDim, fontSize: 11 }}>{open ? "▲" : "▼"}</span>
        )}
      </div>
      {open && status === "done" && breaches.length > 0 && (
        <div style={{ borderTop: `1px solid ${C.border}`, maxHeight: 320, overflowY: "auto" }}>
          {breaches.map((b, i) => (
            <div key={i} style={{ padding: "10px 14px", borderBottom: i < breaches.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ color: C.danger, fontSize: 12, fontWeight: 600 }}>{b.Name}</span>
                <span style={{ color: C.textDim, fontSize: 10 }}>{b.BreachDate}</span>
                {b.IsVerified && <span style={{ color: C.warn, fontSize: 10 }}>Verified</span>}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                {(b.DataClasses || []).map(dc => (
                  <span key={dc} style={{ padding: "1px 6px", background: "rgba(255,68,68,0.1)",
                    border: "1px solid rgba(255,68,68,0.25)", borderRadius: 4, color: C.danger, fontSize: 10 }}>
                    {dc}
                  </span>
                ))}
              </div>
              <div style={{ color: C.textDim, fontSize: 10 }}>{(b.Description || "").replace(/<[^>]+>/g, "").slice(0, 200)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhoneCard({ status, data, error, C }) {
  const isValid   = data?.phone_validation?.is_valid;
  const riskLevel = data?.phone_risk?.risk_level;
  const riskColor = riskLevel === "high" ? C.danger : riskLevel === "medium" ? C.warn : C.success;

  const statusEl = status === "loading" ? (
    <span style={{ color: C.textDim, fontSize: 11 }}>⏳ Validating…</span>
  ) : status === "error" ? (
    <span style={{ color: C.danger, fontSize: 11 }}>✗ {error}</span>
  ) : status === "done" ? (
    <span style={{ color: isValid ? C.success : C.warn, fontSize: 11 }}>
      {isValid ? `✓ Valid — ${data.phone_carrier?.line_type || ""}` : "⚠ Invalid / unallocated"}
    </span>
  ) : (
    <span style={{ color: C.textDim, fontSize: 11 }}>Not yet run</span>
  );

  const rows = status === "done" && data ? [
    ["Number",        data.phone_format?.international || data.phone_number],
    ["Line Type",     data.phone_carrier?.line_type],
    ["Carrier",       data.phone_carrier?.name],
    ["Country",       data.phone_location?.country_name],
    ["Region",        data.phone_location?.region !== data.phone_location?.city ? data.phone_location?.region : null],
    ["Line Status",   data.phone_validation?.line_status],
    ["VoIP",          data.phone_validation?.is_voip ? "Yes" : "No"],
    ["Risk Level",    riskLevel],
    ["Disposable",    data.phone_risk?.is_disposable ? "Yes" : "No"],
    ["Abuse Flag",    data.phone_risk?.is_abuse_detected ? "Yes" : "No"],
    ["Breaches",      data.phone_breaches?.total_breaches ? String(data.phone_breaches.total_breaches) : null],
  ].filter(([, v]) => v != null) : [];

  return (
    <div style={{ background: C.surface, border: `1px solid ${status === "done" && riskLevel === "high" ? C.danger + "44" : C.border}`, borderRadius: 8, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 15 }}>☎</span>
        <span style={{ color: C.text, fontSize: 12, fontWeight: 600, flex: 1 }}>Phone Intelligence (Abstract API)</span>
        {statusEl}
      </div>
      {status === "done" && rows.length > 0 && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: "12px 14px" }}>
          {rows.map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: 8, marginBottom: 5 }}>
              <span style={{ color: C.textDim, fontSize: 11, minWidth: 90, flexShrink: 0 }}>{k}</span>
              <span style={{ color: k === "Risk Level" ? riskColor : C.textMid, fontSize: 11 }}>{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function ExternalResearch({ subjectName, subjectType = "Individual", subjectCountry = "", compact = false }) {
  const C = useTheme();
  const { t } = useTranslation();

  // Per-category state: null | "loading" | "done" | "error"
  const [statuses,   setStatuses]   = useState({ web: null, news: null, court: null, social: null, hibp: null, phone: null });
  const [results,    setResults]    = useState({ web: null, news: null, court: null, social: null });
  const [hibpData,   setHibpData]   = useState(null);
  const [phoneData,  setPhoneData]  = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [errors,     setErrors]     = useState({});
  const [aiText,     setAiText]     = useState("");
  const [aiLoading,  setAiLoading]  = useState(false);
  const [aiError,    setAiError]    = useState("");
  const [customQuery,setCustomQuery]= useState(subjectName || "");

  const name = customQuery.trim() || subjectName;

  function setStatus(cat, val)  { setStatuses(p => ({ ...p, [cat]: val })); }
  function setResult(cat, data) { setResults(p =>  ({ ...p, [cat]: data })); }
  function setError(cat, msg)   { setErrors(p =>   ({ ...p, [cat]: msg })); }

  async function runCategory(cat) {
    if (!name) return;
    setStatus(cat, "loading");
    setError(cat, "");
    try {
      const { results: r } = await webSearch(name, cat);
      setResult(cat, r);
      setStatus(cat, "done");
    } catch (e) {
      setError(cat, e.message);
      setStatus(cat, "error");
    }
  }

  async function runHibp() {
    const email = emailInput.trim();
    if (!email) return;
    setStatus("hibp", "loading");
    setError("hibp", "");
    setHibpData(null);
    try {
      const data = await hibpLookup(email);
      setHibpData(data);
      setStatus("hibp", "done");
    } catch (e) {
      setError("hibp", e.message);
      setStatus("hibp", "error");
    }
  }

  async function runPhone() {
    const phone = phoneInput.trim();
    if (!phone) return;
    setStatus("phone", "loading");
    setError("phone", "");
    setPhoneData(null);
    try {
      const data = await phoneLookup(phone);
      setPhoneData(data);
      setStatus("phone", "done");
    } catch (e) {
      setError("phone", e.message);
      setStatus("phone", "error");
    }
  }

  async function runAll() {
    if (!name) return;
    await Promise.allSettled([
      runCategory("web"),
      runCategory("news"),
      runCategory("court"),
      runCategory("social"),
      ...(emailInput.trim() ? [runHibp()] : []),
      ...(phoneInput.trim() ? [runPhone()] : []),
    ]);
  }

  async function runAiSynthesis() {
    if (!name) return;
    setAiLoading(true);
    setAiError("");
    try {
      const allResultsForAI = {
        web:   results.web,
        news:  results.news,
        court: results.court,
        social:results.social,
        hibp:  hibpData,
        phone: phoneData,
      };
      const text = await aiSynthesize(name, subjectType, subjectCountry, allResultsForAI);
      setAiText(text);
    } catch (e) {
      setAiError(e.message);
    }
    setAiLoading(false);
  }

  const anyRunning = Object.values(statuses).some(s => s === "loading");
  const anyDone    = Object.values(statuses).some(s => s === "done");
  const totalFound = (results.web?.length || 0) + (results.news?.length || 0) + (results.court?.length || 0) + (results.social?.length || 0)
    + (hibpData && !hibpData.clean ? hibpData.breaches.length : 0)
    + (phoneData?.phone_risk?.risk_level === "high" ? 1 : 0);

  const cardProps = (cat, title, icon) => ({
    title, icon, C,
    status:  statuses[cat],
    results: results[cat],
    error:   errors[cat],
  });

  return (
    <div style={{ fontFamily: "'DM Mono', monospace" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", align: "center", gap: 10, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
          {/* Subject search input */}
          <input
            value={customQuery}
            onChange={e => setCustomQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !anyRunning && runAll()}
            placeholder={subjectName || "Subject name…"}
            style={{ background: "rgba(0,0,0,0.2)", border: `1px solid ${C.border}`, borderRadius: 7,
              color: C.text, fontSize: 12, padding: "7px 12px", fontFamily: "'DM Mono',monospace",
              outline: "none", width: 260, flexShrink: 0 }}
          />
          <button onClick={runAll} disabled={anyRunning || !name}
            style={{ padding: "7px 16px", borderRadius: 7, background: C.accent, color: C.bg,
              border: "none", cursor: anyRunning ? "not-allowed" : "pointer",
              fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700,
              opacity: anyRunning ? 0.6 : 1, whiteSpace: "nowrap" }}>
            {anyRunning ? "⏳ Searching…" : "◈ Run All Research"}
          </button>
          {anyDone && !aiText && !aiLoading && (
            <button onClick={runAiSynthesis}
              style={{ padding: "7px 14px", borderRadius: 7, background: "rgba(167,139,250,0.15)",
                border: "1px solid rgba(167,139,250,0.4)", color: "#A78BFA",
                cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
              ✦ AI Synthesis
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
          {anyDone && (
            <span style={{ color: totalFound > 0 ? C.warn : C.success, fontSize: 11 }}>
              {totalFound > 0 ? `${totalFound} total results` : "No adverse findings"}
            </span>
          )}
        </div>
      </div>

      {/* Email & Phone lookup inputs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flex: 1, minWidth: 220 }}>
          <input
            value={emailInput}
            onChange={e => setEmailInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !anyRunning && emailInput.trim() && runHibp()}
            placeholder="Email address (HIBP breach check)…"
            type="email"
            style={{ flex: 1, background: "rgba(0,0,0,0.2)", border: `1px solid ${statuses.hibp === "error" ? C.danger : statuses.hibp === "done" ? C.accent : C.border}`,
              borderRadius: 7, color: C.text, fontSize: 12, padding: "7px 12px",
              fontFamily: "'DM Mono',monospace", outline: "none" }}
          />
          <button onClick={runHibp} disabled={anyRunning || !emailInput.trim()}
            style={{ padding: "7px 12px", borderRadius: 7,
              background: statuses.hibp === "done" ? C.accentGlow : "transparent",
              border: `1px solid ${statuses.hibp === "error" ? C.danger : statuses.hibp === "done" ? C.accent : C.border}`,
              color: statuses.hibp === "error" ? C.danger : statuses.hibp === "done" ? C.accent : C.textDim,
              cursor: anyRunning || !emailInput.trim() ? "not-allowed" : "pointer",
              fontFamily: "'DM Mono',monospace", fontSize: 11,
              opacity: anyRunning || !emailInput.trim() ? 0.5 : 1, whiteSpace: "nowrap" }}>
            {statuses.hibp === "loading" ? "⏳" : "✉ HIBP"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 6, flex: 1, minWidth: 220 }}>
          <input
            value={phoneInput}
            onChange={e => setPhoneInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !anyRunning && phoneInput.trim() && runPhone()}
            placeholder="Phone number (e.g. +12025551234)…"
            type="tel"
            style={{ flex: 1, background: "rgba(0,0,0,0.2)", border: `1px solid ${statuses.phone === "error" ? C.danger : statuses.phone === "done" ? C.accent : C.border}`,
              borderRadius: 7, color: C.text, fontSize: 12, padding: "7px 12px",
              fontFamily: "'DM Mono',monospace", outline: "none" }}
          />
          <button onClick={runPhone} disabled={anyRunning || !phoneInput.trim()}
            style={{ padding: "7px 12px", borderRadius: 7,
              background: statuses.phone === "done" ? C.accentGlow : "transparent",
              border: `1px solid ${statuses.phone === "error" ? C.danger : statuses.phone === "done" ? C.accent : C.border}`,
              color: statuses.phone === "error" ? C.danger : statuses.phone === "done" ? C.accent : C.textDim,
              cursor: anyRunning || !phoneInput.trim() ? "not-allowed" : "pointer",
              fontFamily: "'DM Mono',monospace", fontSize: 11,
              opacity: anyRunning || !phoneInput.trim() ? 0.5 : 1, whiteSpace: "nowrap" }}>
            {statuses.phone === "loading" ? "⏳" : "☎ Phone"}
          </button>
        </div>
      </div>

      {/* Individual category buttons */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {[
          { cat: "web",    label: "🌐 Web / Business", },
          { cat: "news",   label: "📰 Adverse News", },
          { cat: "court",  label: "⚖ Court Records", },
          { cat: "social", label: "👤 Social Media", },
        ].map(({ cat, label }) => (
          <button key={cat} disabled={anyRunning || !name}
            onClick={() => runCategory(cat)}
            style={{ padding: "5px 10px", borderRadius: 6, fontSize: 11,
              background: statuses[cat] === "done" ? C.accentGlow : "transparent",
              border: `1px solid ${statuses[cat] === "error" ? C.danger : statuses[cat] === "done" ? C.accent : C.border}`,
              color: statuses[cat] === "error" ? C.danger : statuses[cat] === "done" ? C.accent : C.textDim,
              cursor: anyRunning ? "not-allowed" : "pointer", fontFamily: "'DM Mono',monospace",
              opacity: anyRunning ? 0.5 : 1, whiteSpace: "nowrap" }}>
            {statuses[cat] === "loading" ? "⏳ …" : label}
          </button>
        ))}
      </div>

      {/* Result cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ResultCard {...cardProps("web",    "Web / Business Research",            "🌐")} />
        <ResultCard {...cardProps("news",   "Adverse News & Negative Media",      "📰")} />
        <ResultCard {...cardProps("court",  "Court Records & Legal Proceedings",  "⚖")} />
        <ResultCard {...cardProps("social", "Social Media Profiles",              "👤")} />
        <HIBPCard status={statuses.hibp} data={hibpData} error={errors.hibp} C={C} />
        <PhoneCard status={statuses.phone} data={phoneData} error={errors.phone} C={C} />
      </div>

      {/* AI Synthesis */}
      {(aiText || aiLoading || aiError) && (
        <div style={{ marginTop: 12, background: "rgba(167,139,250,0.06)",
          border: "1px solid rgba(167,139,250,0.25)", borderRadius: 8, padding: "14px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ color: "#A78BFA", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em" }}>
              ✦ AI RISK SYNTHESIS
            </div>
            {aiText && !aiLoading && (
              <button onClick={runAiSynthesis}
                style={{ padding: "3px 9px", borderRadius: 5, background: "transparent",
                  border: "1px solid rgba(167,139,250,0.3)", color: "#A78BFA",
                  cursor: "pointer", fontSize: 10, fontFamily: "'DM Mono',monospace" }}>
                ↻ Re-run
              </button>
            )}
          </div>
          {aiLoading && (
            <div style={{ color: C.textDim, fontSize: 12 }}>⏳ Synthesizing research findings…</div>
          )}
          {aiError && (
            <div style={{ color: C.danger, fontSize: 12 }}>✗ {aiError}</div>
          )}
          {aiText && !aiLoading && (
            <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
              {aiText}
            </div>
          )}
        </div>
      )}

      {/* Idle state */}
      {!anyDone && !anyRunning && (
        <div style={{ padding: "20px", textAlign: "center", color: C.textDim, fontSize: 12,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 8 }}>
          Click <span style={{ color: C.accent }}>◈ Run All Research</span> to search web, adverse news, court records, and social media for this subject.
        </div>
      )}
    </div>
  );
}
